import React from 'react';
import { useUserStore } from '../../stores/userStore';
import { useUserStats } from '../../hooks/useUserStats';
import { useCurrentView } from '../../hooks/useCurrentView';
import { useTheme } from '../../hooks/useTheme';
import { getUserRank } from '../../utils/levelCalculation';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { selectionGlow } from '../../utils/selectionGlow';
import { RailReward } from './RailReward';

interface UserProgressProps {
  isCollapsed?: boolean;
  /** Called after navigating to the profile — used to close the mobile drawer. */
  onNavigate?: () => void;
  /** Show the desktop XP reward overlay on the ring (rail only, not the drawer). */
  enableReward?: boolean;
  /**
   * ⚠️ The desktop rail turns this OFF and draws the divider itself, one level
   * up — so the rail's Wallet sits INSIDE the identity block (avatar · level ·
   * rank · balance) rather than below the line that closes it. The mobile
   * drawer keeps the default, where this block really is the last thing.
   */
  showDivider?: boolean;
}

/**
 * The identity block's shell — SHARED by the expanded and collapsed rails.
 *
 * ⭐⭐ **`min-h-[244px]` IS WHAT KEEPS THE FIVE DESTINATIONS IN THE SAME PLACE
 * WHEN THE RAIL COLLAPSES** (owner, 2026-08-19). MEASURED, not calculated: with
 * `showDivider={false}` (how the rail always renders it) this block is 244px
 * expanded and 115px collapsed, because collapsing swaps a 120px avatar for a
 * 40px one and drops the name and the rank badge. Without a floor, Home · Build
 * · Discover · Reflect · Assistant jumped 129px up the rail every time it
 * narrowed — the icons landing nowhere near the labels they replace.
 *
 * ⚠️ A MIN, not a fixed height: the expanded block must still be free to grow.
 * ⚠️⚠️ It is therefore NOT self-correcting. Add a row to the expanded identity
 * block and the two drift apart again — re-measure and raise this number.
 * ⚠️ Inert in `NavDrawer`, which renders with the divider (256px) and is never
 * collapsed, so the floor never binds there.
 *
 * ⭐ `justify-center` centres the COLLAPSED content in the space the floor
 * reserves (owner). It is a no-op when expanded, where the content is exactly
 * as tall as the floor.
 */
const IDENTITY_SHELL = 'flex flex-col items-center justify-center pt-6 min-h-[244px]';

export function UserProgress({ isCollapsed = false, onNavigate, enableReward = false, showDivider = true }: UserProgressProps) {
  const { name, initials, avatarUrl } = useUserStore();
  const { stats } = useUserStats();
  const { setCurrentView, currentView } = useCurrentView();
  const { primaryColorCSS: primaryColor, isMetanoia } = useTheme();
  // The profile is the "selected" item while you're on the status/profile page —
  // glow the avatar instead of drawing a flat ring. On the Metanoia brand theme
  // the glow is the brand GRADIENT (a blurred halo, see below); other themes use
  // a solid glow in the accent colour.
  const profileActive = currentView === 'status';
  const showGradientHalo = profileActive && isMetanoia;
  const avatarGlow = profileActive && !isMetanoia ? selectionGlow(primaryColor) : undefined;
  const userRank = getUserRank(stats.level);
  const railRewardOn = useProgressDisplayStore((s) => s.desktopSurfaces.includes('rail'));
  const showReward = enableReward && railRewardOn;
  
  // Size constants for both views
  const expandedSize = 120;
  const collapsedSize = 40;
  const strokeWidth = isCollapsed ? 2 : 4;
  
  const getCircleConfig = (size: number) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    // Ensure progress is a valid number between 0 and 100
    const progress = Math.min(Math.max(isNaN(stats.progress) ? 0 : stats.progress, 0), 100);
    const progressOffset = circumference - (progress / 100) * circumference;
    return { radius, circumference, progressOffset };
  };

  if (isCollapsed) {
    const { radius, circumference, progressOffset } = getCircleConfig(collapsedSize);

    return (
      <div className={`${IDENTITY_SHELL} ${
        // ⚠️ When the rail draws its own divider the WALLET follows immediately,
        // so a full py-6 leaves a gap that reads as a section break inside what
        // is meant to be one identity block.
        showDivider ? 'pb-6 border-b border-border' : 'pb-3'
      }`}>
        <button
          onClick={() => { setCurrentView('status'); onNavigate?.(); }}
          className="relative w-10 h-10 group"
        >
          {showGradientHalo && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{ background: 'var(--primary-gradient)', filter: 'blur(8px)', opacity: 0.6, transform: 'scale(1.08)' }}
            />
          )}
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            {isMetanoia && (
              <defs>
                <linearGradient id="metanoia-grad-sm" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3CC2DD" />
                  <stop offset="50%" stopColor="#A739E2" />
                  <stop offset="100%" stopColor="#F58C56" />
                </linearGradient>
              </defs>
            )}
            <circle
              cx={collapsedSize / 2}
              cy={collapsedSize / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-border opacity-60"
            />
            <circle
              cx={collapsedSize / 2}
              cy={collapsedSize / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={progressOffset}
              strokeLinecap="round"
              stroke={isMetanoia ? 'url(#metanoia-grad-sm)' : primaryColor}
              className="transition-all duration-300 drop-shadow-sm"
            />
          </svg>

          <div
            className="absolute inset-1 rounded-full overflow-hidden bg-muted flex items-center justify-center group-hover:ring-2 group-hover:ring-opacity-75 transition-all shadow-sm"
            style={{ '--tw-ring-color': isMetanoia ? 'transparent' : primaryColor, boxShadow: avatarGlow } as React.CSSProperties}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-sm font-semibold text-muted-foreground">
                {initials}
              </span>
            )}
          </div>

          {showReward && <RailReward size={collapsedSize} strokeWidth={strokeWidth} isCollapsed />}
        </button>

        <span className="mt-2 text-xs font-medium" style={{ color: isMetanoia ? 'rgb(var(--foreground))' : primaryColor }}>
          {stats.level}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {stats.progress}%
        </span>
      </div>
    );
  }

  // Expanded view
  const { radius, circumference, progressOffset } = getCircleConfig(expandedSize);

  return (
    <div className={`${IDENTITY_SHELL} ${
        // ⚠️ When the rail draws its own divider the WALLET follows immediately,
        // so a full py-6 leaves a gap that reads as a section break inside what
        // is meant to be one identity block.
        showDivider ? 'pb-6 border-b border-border' : 'pb-3'
      }`}>
      <button
        onClick={() => { setCurrentView('status'); onNavigate?.(); }}
        className="relative w-[120px] h-[120px] group"
      >
        {showGradientHalo && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{ background: 'var(--primary-gradient)', filter: 'blur(16px)', opacity: 0.55, transform: 'scale(1.04)' }}
          />
        )}
        <svg className="w-full h-full -rotate-90 group-hover:scale-105 transition-transform duration-300">
          {isMetanoia && (
            <defs>
              <linearGradient id="metanoia-grad-lg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3CC2DD" />
                <stop offset="50%" stopColor="#A739E2" />
                <stop offset="100%" stopColor="#F58C56" />
              </linearGradient>
            </defs>
          )}
          <circle
            cx={expandedSize / 2}
            cy={expandedSize / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-border opacity-60"
          />
          <circle
            cx={expandedSize / 2}
            cy={expandedSize / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            strokeLinecap="round"
            stroke={isMetanoia ? 'url(#metanoia-grad-lg)' : primaryColor}
            className="transition-all duration-300"
            filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))"
          />
        </svg>

        <div
          className="absolute inset-3 rounded-full overflow-hidden bg-muted flex items-center justify-center group-hover:ring-2 group-hover:ring-opacity-75 transition-all shadow-md"
          style={{ '--tw-ring-color': isMetanoia ? 'transparent' : primaryColor, boxShadow: avatarGlow } as React.CSSProperties}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-2xl font-semibold text-muted-foreground">
              {initials}
            </span>
          )}
        </div>

        {showReward && <RailReward size={expandedSize} strokeWidth={strokeWidth} />}
      </button>

      {/* ⚠️⚠️ **`whitespace-nowrap` STOPS THE RAIL TWITCHING ON HOVER.** The
          width animates 64px → 192px over 300ms, and this block is laid out at
          every width on the way. Measured: at 64–128px the name wrapped to a
          second line and the block became 264px instead of 244px, so the five
          destinations below were shoved down 20px and snapped back as it
          finished — a vertical judder on every hover.
          ⭐ Clipping is the right trade here: the rail is `overflow-hidden`, so
          a mid-transition label is briefly cut off rather than reflowing
          everything under it. Nothing is clipped once the rail settles. */}
      <div className="mt-4 text-center space-y-1 whitespace-nowrap">
        <h3 className="font-medium text-foreground">{name}</h3>
        <div className="flex items-center justify-center gap-1">
          <span className="text-sm font-medium" style={{ color: isMetanoia ? 'rgb(var(--foreground))' : primaryColor }}>Level {stats.level}</span>
          <span className="text-xs text-muted-foreground">
            ({stats.progress}%)
          </span>
        </div>
        <div
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: `${primaryColor}15`,
            color: isMetanoia ? 'rgb(var(--foreground))' : primaryColor
          }}
        >
          {userRank.name}
        </div>
      </div>
    </div>
  );
}